import { FC, memo } from 'react';
import { Project, ProjectStatus } from '../../types/project';
import styles from './ProjectCard.module.css';

interface ProjectCardProps {
  project: Project;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
}

const getStatusLabel = (status: ProjectStatus): string => {
  const labels: Record<ProjectStatus, string> = {
    danger: '炎上中',
    warning: '炎上の可能性あり',
    good: '良好',
    normal: '普通'
  };
  return labels[status];
};

const getStatusIcon = (status: ProjectStatus): string => {
  const icons: Record<ProjectStatus, string> = {
    danger: '▲',
    warning: '△',
    good: '◯',
    normal: '◯'
  };
  return icons[status];
};

export const ProjectCard: FC<ProjectCardProps> = memo(({ project, onEdit, onDelete }) => {
  const deadline = project.deadline ? new Date(project.deadline) : null;
  const formattedDeadline = deadline ? deadline.toLocaleDateString('ja-JP') : '未設定';
  const deadlineClass = project.delay_days > 0 ? styles.deadlineWarning : '';

  return (
    <div className={`${styles.card} level-${project.status}`}>
      <div className={styles.header}>
        <h3 className={styles.title}>{project.Name}</h3>
        <div className={`${styles.deadline} ${deadlineClass}`}>
          期限: {formattedDeadline}
        </div>
        <div className={styles.actions}>
          <button
            className={styles.editButton}
            onClick={() => onEdit(project)}
          >
            編集
          </button>
          <button
            className={styles.deleteButton}
            onClick={() => onDelete(project)}
          >
            削除
          </button>
        </div>
      </div>
      <div className={styles.metrics}>
        <div className={styles.metric}>
          <div className={styles.metricHeader}>
            <span className={styles.label}>進捗率</span>
            <span className={`${styles.value} ${styles[`value${project.status}`]}`}>
              {project.progress_rate}%
            </span>
          </div>
          <div className={styles.progressBar}>
            <div
              className={`${styles.progress} ${styles[`progress${project.status}`]}`}
              style={{ width: `${project.progress_rate}%` }}
            />
          </div>
        </div>
        {project.memo && (
          <div className={styles.memo}>
            <div className={styles.memoHeader}>メモ</div>
            <div className={styles.memoContent}>{project.memo}</div>
          </div>
        )}
        <div className={`${styles.statusIndicator} ${styles[`status${project.status}`]}`}>
          <span className={styles.statusLabel}>
            {getStatusLabel(project.status)}
          </span>
          <span className={styles.statusIcon}>
            {getStatusIcon(project.status)}
          </span>
        </div>
      </div>
    </div>
  );
});

ProjectCard.displayName = 'ProjectCard';